import { extractInterviewQuestions } from '../../shared/extractInterviewQuestions';
import type { ScriptStatus } from '../../shared/types';
import { loadPdfImages } from './pdfImages';

// PDF rendering pulls in @react-pdf/renderer which is a heavy dep
// (~350 KB minified). Loading it eagerly bloats every first paint, even
// for users who never click Download PDF. Dynamic imports below let Vite
// split the renderer into its own chunk that only ships on demand.

export interface DownloadPdfInput {
  title: string;
  client_name: string;
  status: ScriptStatus;
  approved_at: number | null;
  slug: string;
  doc_json: string;
}

/**
 * Generate a branded PDF and trigger the browser download. Used by both the
 * admin edit page and the public client review page.
 */
export async function downloadScriptPdf(input: DownloadPdfInput): Promise<void> {
  const { renderScriptToBlob, setPdfImages } = await import('../components/pdf/HSPScriptPDF');
  const doc = JSON.parse(input.doc_json);
  setPdfImages(await loadPdfImages(doc));
  const blob = await renderScriptToBlob({
    title: input.title,
    client_name: input.client_name,
    status: input.status,
    approved_at: input.approved_at,
    slug: input.slug,
    doc,
  });

  const filename = `${sanitize(input.client_name)} — ${sanitize(input.title)}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Free the blob URL after a short delay so the browser had time to start
  // the download.
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

function sanitize(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, '').trim() || 'Script';
}

export interface DownloadBriefInput extends DownloadPdfInput {
  /** 'client' is the safe default everywhere: client fields only. */
  variant: 'client' | 'full';
}

/**
 * Generate an interview brief PDF and trigger the browser download. The
 * client page only ever asks for the 'client' variant; the admin page offers
 * both behind a split button, so the filename says which one landed in the
 * downloads folder.
 */
export async function downloadBriefPdf(input: DownloadBriefInput): Promise<void> {
  const [{ renderBriefToBlob }, { setPdfImages }] = await Promise.all([
    import('../components/pdf/HSPBriefPDF'),
    import('../components/pdf/HSPScriptPDF'),
  ]);
  const doc = JSON.parse(input.doc_json);
  // The brief renderer projects the doc for the client copy; an image in an
  // internal field is fetched (admin only) but never printed.
  setPdfImages(await loadPdfImages(doc));
  const blob = await renderBriefToBlob({
    title: input.title,
    client_name: input.client_name,
    status: input.status,
    approved_at: input.approved_at,
    slug: input.slug,
    doc,
    variant: input.variant,
  });

  const filename = briefPdfFilename(input.client_name, input.title, input.variant);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

export interface DownloadInterviewInput {
  title: string;
  client_name: string;
  slug: string;
  doc_json: string;
}

/**
 * Generate a phone-friendly shoot-day PDF of just the Interview questions
 * section and trigger a browser download. Used by the admin Shoot view —
 * gives Henry an offline reference he can keep on a phone or printed sheet
 * when on-set internet is unreliable.
 */
export async function downloadInterviewPdf(input: DownloadInterviewInput): Promise<void> {
  const { renderInterviewToBlob } = await import('../components/pdf/HSPInterviewPDF');
  const questions = extractInterviewQuestions(input.doc_json) ?? [];
  const blob = await renderInterviewToBlob({
    title: input.title,
    client_name: input.client_name,
    slug: input.slug,
    questions,
  });

  const filename = `${sanitize(input.client_name)} — ${sanitize(input.title)} — shoot view.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

/** The brief's filename says which copy landed in the downloads folder. */
export function briefPdfFilename(
  clientName: string,
  title: string,
  variant: 'client' | 'full',
): string {
  const suffix = variant === 'client' ? 'client copy' : 'full brief';
  return `${sanitize(clientName)} — ${sanitize(title)} — ${suffix}.pdf`;
}

export interface DownloadCertificateInput {
  title: string;
  clientName: string;
  approvedByName: string;
  approvedAtMs: number;
  status: ScriptStatus;
  slug: string;
}

/**
 * Generate the brand-matched approval certificate PDF and trigger a browser
 * download. Used from the approval ceremony's confirmation state.
 */
export async function downloadApprovalCertificate(
  input: DownloadCertificateInput,
): Promise<void> {
  const { renderApprovalCertificateToBlob } = await import(
    '../components/pdf/HSPApprovalCertificatePDF'
  );
  const blob = await renderApprovalCertificateToBlob(input);

  const filename = `${sanitize(input.clientName)} — ${sanitize(input.title)} — approval certificate.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}
